import { Component, useContext } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { Button } from 'reactstrap';
import { Loader } from 'components/Loader';
import { NotificationContext, NotificationContextState } from 'context/NotificationContext';
import { triggerDownloadBlob } from 'logic/download';
import Select from 'react-select';
import { ChartBar } from 'components/ChartBar';
import cx from 'classnames';

import css from './TabSystemUsage.module.scss';

import * as apiCalls from 'logic/apiCalls';
import log from 'loglevel';

import { RangeOption, rangeOptions } from 'logic/billingMetricLogic';
import { BillingMetricInfo, ChartData, ChartInfo, createChartSubText } from 'logic/billingMetricLogic';
import * as misc from 'utils/misc';

type TabSystemUsageInternalProps = {
    notificationContext: NotificationContextState
}

type TabSystemUsageInternalState = {
    isLoading: boolean
    isUpdating: boolean
    showApiError: boolean
    selectedGroupOption: RangeOption
    selectedRange: string
    resolution: string
    showStacked: boolean
}

class TabSystemUsageInternal extends Component<TabSystemUsageInternalProps, TabSystemUsageInternalState> {
    state: TabSystemUsageInternalState = {
        isLoading: true,
        isUpdating: false,
        showApiError: false,
        selectedGroupOption: rangeOptions[0],
        selectedRange: rangeOptions[0].value,
        resolution: rangeOptions[0].resolution,
        showStacked: rangeOptions[0].stacked
    }
    metricInfos: any[] = [];
    metricData: any[] = [];
    metricStartDate?: string | null = null;
    metricEndDate?: string | null = null;

    downloadExcel = async () => {
        try {
            const configurationScriptBlob = await apiCalls.getSystemUsageReport(this.state.selectedRange);
            triggerDownloadBlob(configurationScriptBlob, 'system_usage_report.xlsx');
        }
        catch (error) {
            log.error(`Something went wrong: ${error}`);
            this.props.notificationContext.setNotification("Error", `Error during download of the usage report.`, 'danger');
        }
    }
    async updateMetrics() {
        try {
            const metricsInfoPromise = apiCalls.getMetricInfos();
            const metricsPromise = apiCalls.getSystemMetrics(this.state.selectedRange);

            const [metricInfos, systemMetrics] = await Promise.all([metricsInfoPromise, metricsPromise]);

            this.metricInfos = metricInfos;
            const billingInfos: BillingMetricInfo[] = [];
            for (const metricInfo of metricInfos) {
                const data: ChartData[] = [];
                for (const distributor of systemMetrics.distributors) {
                    let val = 0.00;
                    if (metricInfo.metric in distributor.totals) {
                        val = misc.round(distributor.totals[metricInfo.metric].metricTotalValue, 2)
                    }
                    data.push({
                        date: distributor.distributorLabel,
                        value: val
                    });
                    this.metricStartDate = distributor.startDate;
                    this.metricEndDate = distributor.endDate;
                }
                const chartInfo: ChartInfo = {
                    data: data,
                    definitions: [{
                        displayName: metricInfo.unitOfMeasure,
                        valueName: "value"
                    }]
                };
                billingInfos.push({
                    displayName: metricInfo.displayName,
                    metric: chartInfo
                });
            }
            this.metricData = billingInfos;

            // this.metricData = billingMetricLogic.convertToChartData(metricInfos, metrics);
            // this.metricStartDate = metrics.startDate;
            // this.metricEndDate = metrics.endDate;

            this.setState({
                isLoading: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Error during retrieval of the billing information:${error}`);

            this.setState({
                isLoading: false,
                showApiError: true
            });
        }
    }
    handleRangeChange = async (newValue: RangeOption | null) => {
        if (!newValue) return;

        try {
            this.setState({
                selectedGroupOption: newValue,
                selectedRange: newValue.value,
                resolution: newValue.resolution,
                showStacked: newValue.stacked,
                isUpdating: true,
                showApiError: false
            }, async () => await this.updateMetrics());

            this.setState({
                isUpdating: false,
                showApiError: false
            });
        }
        catch (error) {
            log.error(`Error during updating of the usage range:${error}`);

            this.setState({
                isUpdating: false,
                showApiError: true
            });
        }
    }
    createChartSubTextJsx = (chartData: any[], unitDisplayName: string): JSX.Element => {

        let [text, val] = createChartSubText(chartData, unitDisplayName);

        return<>{text} in this period:<b>{val}</b></>
    }

    async componentDidMount() {
        await this.updateMetrics();
    }

    render() {
        return (
            <Loader isLoading={false} showApiError={false}>
                <>
                    <div className={css.rangeSelect}>
                        <Select
                            className="my-3"
                            inputValue=''
                            value={this.state.selectedGroupOption}
                            options={rangeOptions}
                            onChange={this.handleRangeChange}
                            placeholder='Select range...' />
                    </div>

                    <div className="my-3">Click the button below to download the system usage information Excel sheet.</div>
                    <Button className='mt-3' color='primary' onClick={this.downloadExcel}>Download</Button>
                    {this.metricData.map(entry =>
                        <Row key={entry.displayName}>

                            <Col lg>
                                <Card className={cx("my-3", css.card)}>
                                    <Card.Header><h5>{entry.displayName}</h5>from {this.metricStartDate?.toString()} until {this.metricEndDate?.toString()}</Card.Header>
                                    <Card.Body>
                                        <ChartBar data={entry.metric.data} keyName="date" bars={entry.metric.definitions} isLoading={this.state.isUpdating} />
                                        {this.createChartSubTextJsx(entry.metric.data, entry.metric.definitions[0].displayName)}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </>
            </Loader>
        )
    }
}

// Inject the required contexts.
export const TabSystemUsage = (props: any) => {
    const notificationContext = useContext(NotificationContext);

    return (
        <TabSystemUsageInternal {...props} notificationContext={notificationContext} />
    )
}